import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import OrderSummary from "./OrderSummary";
import CompactSegmentCard from "./CompactSegmentCard";
import { useOrderCalculations } from "@/hooks/useOrderCalculations";
import { mapEtapaToSegment, isDeferredProgrammedOrderItem } from "@/lib/utils";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import ConsolidatedSummary from "./ConsolidatedSummary";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";
import type { CouponValidation } from "@/lib/api";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

interface TimelineSummaryProps {
  cycleYear?: number;
  orderItems: OrderItem[];
  fullOrderItems: OrderItem[]; // New prop
  formatCurrency: ( value: number ) => string;
  finalizarPedido: ( reason?: string, coupon?: CouponValidation | null ) => void;
  getGrossSubtotal: () => number;
  getSubtotal: () => number;
  getTotal: () => number;
  freteAnual: number; // Added
  freteSemestral: number; // Added
  shippingFee?: number;
  isScheduled: boolean;
  isBackofficeEdit?: boolean;
  isEditMode?: boolean;
  onAnnualInstallmentsChange?: ( value: number ) => void;
  onSemestralInstallmentsChange?: ( value: number ) => void;
  onAnnualConditionChange?: ( value: string | null ) => void;
  onSemestralConditionChange?: ( value: string | null ) => void;
  isReadOnly?: boolean; // Ciclo em fechamento: esconde finalização e plano de parcelas
  shippingAddressSlot?: React.ReactNode; // Endereço de entrega, exibido no resumo de valores acima do cupom
}

const TimelineSummary = ( {
  cycleYear,
  orderItems,
  fullOrderItems, // Destructure new prop
  isScheduled,
  formatCurrency,
  finalizarPedido,
  getGrossSubtotal,
  getSubtotal,
  getTotal,
  freteAnual, // Adicionado
  freteSemestral, // Adicionado
  shippingFee,
  isBackofficeEdit = false,
  isEditMode = false,
  onAnnualInstallmentsChange,
  onSemestralInstallmentsChange,
  onAnnualConditionChange,
  onSemestralConditionChange,
  isReadOnly = false,
  shippingAddressSlot,
}: TimelineSummaryProps ) => {
  // Cupom por itens: desconto por produto informado pelo resumo de valores, abatido nos cartões por segmento.
  const [ couponDiscountByProduct, setCouponDiscountByProduct ] = React.useState<Record<number, number>>( {} );
  // The main calculations are passed via props (getTotal, getShippingTotal, etc.)
  // This local calculation is only for grouping, so passing shippingFee here ensures consistency
  // if we ever decide to use locally calculated values.
  const { groupProductsByBrand } = useOrderCalculations( orderItems, isScheduled, shippingFee || 0 );

  // console.log( 'TimelineSummary ==> \n', {
  //   orderItems,
  //   fullOrderItems, // Destructure new prop
  //   isScheduled,
  //   formatCurrency,
  //   finalizarPedido,
  //   getGrossSubtotal,
  //   getSubtotal,
  //   getTotal,
  //   freteAnual, // Adicionado
  //   freteSemestral, // Adicionado
  //   shippingFee,
  // } )

  const isProductBonusEnabled = isProductBonusEnabledForCycle(cycleYear);

  // When bonuses are active, teacher totals have their own summary card.
  // Mesmo critério do total do pedido: no pedido normal, itens programados não são cobrados agora
  const segmentItems = orderItems.filter( ( item ) => !(
    !isScheduled
    && isNewScheduledOrderCycleEnabledForCycle( cycleYear )
    && isDeferredProgrammedOrderItem( item, cycleYear )
  ) );

  const itemsBySegment = segmentItems.reduce( ( acc, item ) => {
    const segment = isProductBonusEnabled && item.utilizacao === "Professor"
      ? "Professor"
      : mapEtapaToSegment( item.serie );
    // console.log( 'segment ==> ', segment )
    if ( !acc[ segment ] ) {
      acc[ segment ] = [];
    }
    acc[ segment ].push( item );
    return acc;
  }, {} as Record<string, OrderItem[]> );

  if (isProductBonusEnabled && !itemsBySegment.Professor) {
    itemsBySegment.Professor = [];
  }

  // Get segment icon based on segment name
  const getSegmentIcon = ( segment: string ) => {
    const icons: Record<string, string> = {
      "Ed. Infantil": "🧸",
      "Fund. Anos Iniciais": "📚",
      "Fund. Anos Finais": "📖",
      "Ensino Médio": "🎓",
      Professor: "👨‍🏫",
      PV: "💼",
      Outros: "📦",
    };
    return icons[ segment ] || "📦";
  };

  // Get segment order for consistent sorting
  const getSegmentOrder = ( segmento: string ): number => {
    const order: Record<string, number> = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      PV: 5,
      Professor: 6,
      Outros: 999,
    };
    return order[ segmento ] || 999;
  };

  // Sort segments by their defined order
  const sortedSegments = Object.keys( itemsBySegment ).sort(
    ( a, b ) => getSegmentOrder( a ) - getSegmentOrder( b )
  );

  // Get dynamic grid classes based on number of segments
  const getDynamicGridClasses = ( segmentCount: number ): string => {
    switch ( segmentCount ) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-1 sm:grid-cols-2";
      case 3:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
      case 4:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
      default:
        // For 5+ segments, use auto-fit for maximum flexibility
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";
    }
  };

  const gridClasses = getDynamicGridClasses( sortedSegments.length );

  return (
    <div className="space-y-6">
      {/* Resumo por Segmento com Grid Responsivo Dinâmico */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            📊 Resumo por Segmento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${ gridClasses }`}>
            {sortedSegments.map( ( segmento ) => {
              const items = itemsBySegment[ segmento ];
              const icon = getSegmentIcon( segmento );

              return (
                <CompactSegmentCard
                  key={segmento}
                  segmentName={segmento}
                  items={items}
                  formatCurrency={formatCurrency}
                  icon={icon}
                  couponDiscountByProduct={couponDiscountByProduct}
                />
              );
            } )}
          </div>
          {/* {!isScheduled && (
            <div className="mt-4 text-xs text-muted-foreground italic">
              * Os valores exibidos não incluem itens do Pedido Programado (2º
              Semestre).
            </div>
          )} */}
        </CardContent>
      </Card>

      <Separator />

      {/* Componente de Resumo Final */}
      <OrderSummary
        cycleYear={cycleYear}
        grossSubtotal={getGrossSubtotal()}
        netSubtotal={getSubtotal()}
        total={getTotal()}
        freteAnual={freteAnual} // Pass freteAnual
        freteSemestral={freteSemestral} // Pass freteSemestral
        orderItems={orderItems}
        formatCurrency={formatCurrency}
        finalizarPedido={finalizarPedido}
        isScheduled={isScheduled}
        isBackofficeEdit={isBackofficeEdit}
        isEditMode={isEditMode}
        onAnnualInstallmentsChange={onAnnualInstallmentsChange}
        onSemestralInstallmentsChange={onSemestralInstallmentsChange}
        onAnnualConditionChange={onAnnualConditionChange}
        onSemestralConditionChange={onSemestralConditionChange}
        hideFinalizeButton={isReadOnly}
        hideInstallmentPlan={isReadOnly}
        onCouponItemDiscountsChange={setCouponDiscountByProduct}
        shippingAddressSlot={shippingAddressSlot}
      />

      {/* Consolidated Summary */}
      {/* {fullOrderItems.length > 0 && (
        <ConsolidatedSummary
          isScheduled={isScheduled}
          freteAnual={freteAnual}
          freteSemestral={freteSemestral}
          orderItems={orderItems} // This is itemsToDisplay
          formatCurrency={formatCurrency}
          shippingFee={shippingFee}
        />
      )} */}
    </div>
  );
};

export default TimelineSummary;
